/**
 * Supplier Invoices Redux Slice (Cabeceras)
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
export interface SupplierInvoice {
  id: number;
  invoice_number: string;
  entity_id: number;
  carrier_id: number | null;
  booking_id: number | null;
  contract_header_id: number | null;
  invoice_date: string | null;
  due_date: string | null;
  currency_id: number | null;
  exchange_rate: number | null;
  total_amount: number;
  net_amount: number;
  tax_amount: number;
  traffic_type: string | null;
  invoice_status_id: number | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Tipos de respuesta
interface SupplierInvoicesResponse {
  data: SupplierInvoice[];
  totalCount: number;
}
interface SupplierInvoicesState extends FilteredState<SupplierInvoice> {
  selectedInvoice: SupplierInvoice | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
}
const initialState: SupplierInvoicesState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'invoice_date',
  sortDirection: 'desc',
  searchTerm: null,
  selectedInvoice: null,
  creating: false,
  updating: false,
  deleting: false,
};
export const fetchSupplierInvoices = createAsyncThunk<SupplierInvoicesResponse, any>('supplierInvoices/fetchInvoices', async (params) => {
  throw new Error('Not implemented yet');
});
export const fetchSupplierInvoiceById = createAsyncThunk<SupplierInvoice, number>('supplierInvoices/fetchInvoiceById', async (id) => {
  throw new Error('Not implemented yet');
});
export const createSupplierInvoice = createAsyncThunk<SupplierInvoice, Partial<SupplierInvoice>>('supplierInvoices/createInvoice', async (invoice) => {
  throw new Error('Not implemented yet');
});
export const updateSupplierInvoice = createAsyncThunk<{ id: number; data: SupplierInvoice }, { id: number; data: Partial<SupplierInvoice> }>('supplierInvoices/updateInvoice', async ({ id, data }) => {
  throw new Error('Not implemented yet');
});
export const deleteSupplierInvoice = createAsyncThunk<number, number>('supplierInvoices/deleteInvoice', async (id) => {
  throw new Error('Not implemented yet');
});
const supplierInvoicesSlice = createSlice({
  name: 'supplierInvoices',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectInvoice: (state, action: PayloadAction<SupplierInvoice | null>) => {
      state.selectedInvoice = action.payload;
    },
    clearSelection: (state) => {
      state.selectedInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupplierInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchSupplierInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar facturas';
      });
  },
});
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectInvoice, clearSelection } = supplierInvoicesSlice.actions;
export default supplierInvoicesSlice.reducer;
